import { _, PageContext } from '@zto/zpage'

import { commonOptions, SysConfigPaths, TdlDeviceType, TdlOrderInboundStatus, TdlOrderStatus, YesNo } from '@/config'
import { getCommonOptionLabel } from '@/utils'

export default {
  body: {
    type: 'crud',
    searchOn: ['$tdl_order_detail_status_change'],
    actions: {
      query: {
        api: '/sys/admin/order/pageList'
      },
      detail: {
        label: '详情',
        link: {
          title: '订单详情（${data.row.thirdPartyOrderNo}）',
          name: 'tuda.tdl.order.detail',
          path: '/tdl/order/detail',
          query: {
            orderId: '${data.row.orderId}'
          }
        }
      },
      takeout: {
        api: '/sys/admin/order/cancelOrderList',
        message: '确认要执行【取出包裹】操作吗？',
        payload: ({ data: { row } }: any) => {
          return { orderIdList: [row?.orderId] }
        }
      },
      delivered: {
        api: '/sys/admin/order/deliveredOrderList',
        payload: ({ data: { row } }: any) => {
          return { orderIdList: [row?.orderId] }
        },
        message: ({ data: { row } }: PageContext) => {
          return '确认要标记该单【已送达】吗？'
        }
      },
      export: {
        fileName: '订单列表',
        api: {
          url: '/sys/admin/order/pageList/cursor',
          responseTransform: (res: any) => {
            res.list = (res.list || []).map((it: any) => {
              it.orderStatus = getCommonOptionLabel(commonOptions.tdlOrderStatus, it.orderStatus)
              return { ...it }
            })

            return res
          }
        },
        apiParams: { pageSize: 1000 }
      }
    },
    search: {
      labelWidth: 90,
      items: [
        { type: 'input', prop: 'thirdPartyOrderNo', label: '运单号' },
        {
          type: 'fuzzy-select',
          label: '站点',
          prop: 'siteCode',
          api: '/sys/admin/site/enablePage',
          keywordProp: 'keyWord',
          optionValueProp: 'siteCode',
          optionLabelProp: 'siteName',
          triggerFocus: true
        },
        {
          type: 'fuzzy-select',
          label: '无人车',
          prop: 'snCode',
          api: '/sys/admin/device/device-list/listEnabled',
          apiParams: { deviceTypeCode: TdlDeviceType.VEHICLE },
          optionValueProp: 'snCode',
          optionLabelProp: 'name',
          triggerFocus: true
        },
        { type: 'input', prop: 'thirdPartyWaveNo', label: '三方波次号' },
        { type: 'input', prop: 'gridCompartmentCode', label: '格口号' },
        {
          type: 'fuzzy-select',
          label: '小区',
          prop: 'communityCode',
          api: '/sys/admin/api/region/community/listEnabled',
          optionValueProp: 'communityCode',
          optionLabelProp: 'communityName',
          triggerFocus: true
        },
        { type: 'input', prop: 'address', label: '楼栋房号' },
        {
          type: 'select',
          prop: 'orderStatusList',
          label: '订单状态',
          multiple: true,
          options: commonOptions.tdlOrderStatus
        },
        {
          type: 'quick-date-range-picker',
          prop: 'deliveryStartTimeBegin',
          toProp: 'deliveryStartTimeEnd',
          label: '起送时间',
          span: 12
        },
        {
          type: 'quick-date-range-picker',
          prop: 'actualDeliveryTimeBegin',
          toProp: 'actualDeliveryTimeEnd',
          label: '送达时间',
          span: 12
        },
        { type: 'input', prop: 'contactPhone', label: '收件人手机号', span: 5 },
        { type: 'input', prop: 'operatorPhone', label: '操作人手机号', span: 5 },
        {
          type: 'fuzzy-select',
          label: '物流公司',
          prop: 'expressCompanyCode',
          api: '/sys/admin/config/dictItems',
          apiParams: {
            dictPath: SysConfigPaths.EXPRESS_COMPANY
          },
          triggerFocus: true,
          span: 5
        },
        {
          type: 'select',
          prop: 'hasDeliveryPhoto',
          label: '订单照片',
          span: 4,
          options: [
            { label: '有', value: YesNo.YES },
            { label: '无', value: YesNo.NO }
          ]
        }
      ]
    },
    toolbar: {
      items: [{ action: 'export', label: '批量导出' }]
    },
    table: {
      innerAttrs: { rowKey: 'snCode' },
      operation: {
        width: 180,
        items: [
          { action: 'detail' },
          {
            action: 'takeout',
            label: '取出包裹',
            visibleOn: ({ data: { row } }: any) => {
              return ![TdlOrderStatus.CANCEL, TdlOrderStatus.DELIVERED].includes(row?.orderStatus)
            }
          },
          {
            action: 'delivered',
            label: '该单已送达',
            visibleOn: ({ data: { row } }: any) => {
              return ![TdlOrderStatus.CANCEL, TdlOrderStatus.DELIVERED].includes(row?.orderStatus)
            }
          }
        ]
      },
      columns: [
        { label: '运单号', prop: 'thirdPartyOrderNo', width: 180 },
        { label: '物流公司', prop: 'expressCompanyName', width: 100 },
        { label: '站点名称', prop: 'siteName', width: 200 },
        { label: '无人车SN', prop: 'snCode', width: 200 },
        { label: '无人车名称', prop: 'deviceName', width: 200 },
        { label: '格口号', prop: 'gridCompartmentCode', width: 100 },
        { label: '三方波次号', prop: 'thirdPartyWaveNo', width: 200 },
        { label: '兔哒波次号', prop: 'waveNo', width: 280 },
        { label: '小区名称', prop: 'communityName', width: 200 },
        { label: '楼栋房号', prop: 'address', width: 200 },
        { label: '收件人手机号', prop: 'contactPhone', width: 200 },
        { label: '起送时间', prop: 'deliveryStartTime', width: 150 },
        { label: '送达时间', prop: 'actualDeliveryTime', width: 150 },
        { label: '操作人手机号', prop: 'operatorPhone', width: 200 },
        { label: '状态', prop: 'orderStatus', width: 150, formatter: { name: 'enumStr', options: 'tdlOrderStatus' } }
      ]
    }
  }
}
